import { useObserva } from "@morganisbatman/observa-client-react";
import { useRouter } from "expo-router";
import { useEffect, useMemo, useRef, useState } from "react";

import { trpc } from "../trpc";
import { useEffectiveSession } from "../use-effective-session";
import { useSelectedVertical } from "../use-selected-vertical";
import { recordAnonAttempt } from "./anon-attempts";
import {
  type Choice,
  type ChoiceLabel,
  type EntitlementCheck,
  makeSessionId,
  type PracticeScore,
  type Question,
  type SubmitResult,
} from "./types";

interface UsePracticeSessionReturn {
  questions: Question[] | null;
  current: Question | null;
  index: number;
  selected: Choice["label"] | null;
  setSelected: (v: Choice["label"] | null) => void;
  result: SubmitResult | null;
  submitting: boolean;
  score: PracticeScore;
  error: string | null;
  setError: (v: string | null) => void;
  entitlement: EntitlementCheck | null;
  /** True once the session resolves with no signed-in user. Drives the
   * publicSample path + the "sign up to unlock the tutor" nudge. */
  isAnonymous: boolean;
  sessionIdRef: React.MutableRefObject<string>;
  /** Submit the active answer; populates `result` + bumps score. Resolves
   * with the fresh result (null on no-op / error) so callers can act on it
   * without reading the stale `result` state in the same tick. */
  submit: () => Promise<SubmitResult | null>;
  /** Advance to the next question, or fire session_completed + nav. */
  next: (onAdvance: () => void) => void;
}

// Loads the active question batch, owns per-question state (selected,
// result, score), and drives the EXHAUSTED → /diagnostic redirect.
// The orchestrator wires the reset hooks (tutor, report) into the
// advance callback so they clear between questions.
export function usePracticeSession(): UsePracticeSessionReturn {
  const router = useRouter();
  const observa = useObserva();
  const vertical = useSelectedVertical();
  const session = useEffectiveSession();

  const sessionIdRef = useRef<string>(makeSessionId());
  const startedAtRef = useRef<number>(Date.now());
  // Client-side answer key for the anonymous (publicSample) path — that
  // endpoint ships correctLabel/explanation so a logged-out user is
  // graded in-browser without hitting the authed submit procedure.
  const anonAnswersRef = useRef<
    Map<string, { correctLabel: ChoiceLabel; explanation: string }>
  >(new Map());

  // Guards the question load against re-running for a context it has
  // already loaded. The loader fetches a RANDOM batch, so any spurious
  // re-run (a churned hook dependency, the entitlements list resolving a
  // tick after the session) would swap in a different set of questions
  // mid-session — the visible flicker. Keyed on the only inputs that
  // should ever trigger a fresh batch: who's asking and for what exam.
  const loadedKeyRef = useRef<string | null>(null);

  const isAnonymous = !session.isPending && session.user === null;

  const [questions, setQuestions] = useState<Question[] | null>(null);
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<Choice["label"] | null>(null);
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [score, setScore] = useState<PracticeScore>({ correct: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);
  const [entitlement, setEntitlement] = useState<EntitlementCheck | null>(
    null,
  );

  const current = useMemo<Question | null>(
    () => (questions ? (questions[index] ?? null) : null),
    [questions, index],
  );

  useEffect(() => {
    // Wait for the session to resolve before choosing a path — without
    // this an anonymous load could fire the authed branch and 401.
    if (vertical.isLoading || session.isPending) return;
    if (!vertical.verticalSlug) {
      setError("No vertical selected. Pick one from the home page first.");
      return;
    }
    // Only load once per (user, vertical, jurisdiction). Without this an
    // identity-unstable dependency would refetch a new random batch and
    // swap the question out from under the user.
    const loadKey = `${session.user?.id ?? "anon"}|${vertical.verticalSlug}|${
      vertical.jurisdictionCode ?? ""
    }`;
    if (loadedKeyRef.current === loadKey) return;
    loadedKeyRef.current = loadKey;
    void (async () => {
      try {
        if (session.user === null) {
          // Anonymous trial: a client-graded sample. No entitlement check,
          // no authed list/submit — those gate the logged-in funnel.
          const sample = await trpc.questions.publicSample.query({
            verticalSlug: vertical.verticalSlug!,
            jurisdictionCode: vertical.jurisdictionCode ?? undefined,
            count: 5,
          });
          anonAnswersRef.current = new Map(
            sample.map((q) => [
              q.id,
              { correctLabel: q.correctLabel, explanation: q.explanation },
            ]),
          );
          setQuestions(
            sample.map((q) => ({
              id: q.id,
              topicId: "",
              topicLabel: q.topicLabel,
              stem: q.stem,
              choices: q.choices,
              difficulty: 0,
            })),
          );
          setEntitlement(null);
          startedAtRef.current = Date.now();
          void observa.event({
            eventType: "practice.session_started",
            payload: {
              sessionId: sessionIdRef.current,
              verticalSlug: vertical.verticalSlug!,
              jurisdictionCode: vertical.jurisdictionCode ?? null,
              tier: "ANON",
              count: sample.length,
            },
          });
          return;
        }

        const ent = await trpc.entitlement.check.query({
          verticalSlug: vertical.verticalSlug!,
          jurisdictionCode: vertical.jurisdictionCode ?? undefined,
        });

        if (ent.tier === "EXHAUSTED") {
          // EXHAUSTED → diagnostic is its own screen per the 2026
          // design refresh. Route there with vertical/jurisdiction
          // context. Skip setEntitlement to avoid a flash before
          // router.replace takes effect.
          const q: Record<string, string> = {
            vertical: vertical.verticalSlug!,
          };
          if (vertical.jurisdictionCode) {
            q.jurisdiction = vertical.jurisdictionCode;
          }
          router.replace({ pathname: "/diagnostic", params: q });
          return;
        }

        setEntitlement(ent);

        // Free-tier users get at most `remainingFree` more questions in
        // this batch.
        const res = await trpc.questions.list.query({
          verticalSlug: vertical.verticalSlug!,
          jurisdictionCode: vertical.jurisdictionCode ?? undefined,
          count: 10,
        });
        setQuestions(res);
        startedAtRef.current = Date.now();
        void observa.event({
          eventType: "practice.session_started",
          payload: {
            sessionId: sessionIdRef.current,
            verticalSlug: vertical.verticalSlug!,
            jurisdictionCode: vertical.jurisdictionCode ?? null,
            tier: ent.tier,
            count: res.length,
          },
        });
      } catch (err) {
        // Clear the guard so a later dep change (e.g. session resolving)
        // can re-attempt the load rather than staying stuck on the error.
        loadedKeyRef.current = null;
        setError(err instanceof Error ? err.message : "failed to load");
      }
    })();
  }, [
    vertical.isLoading,
    vertical.verticalSlug,
    vertical.jurisdictionCode,
    session.isPending,
    session.user,
    observa,
    router,
  ]);

  // Returns the freshly-computed result (or null on no-op / error) so the
  // orchestrator can react to it WITHOUT reading the `result` state, which
  // is stale inside the same tick's closures (the React state update from
  // setResult isn't visible until the next render). This is what drives the
  // wrong-answer tutor kickoff in practice.tsx.
  async function submit(): Promise<SubmitResult | null> {
    if (!current || !selected) return null;
    setSubmitting(true);
    setError(null);
    const msSpent = Date.now() - startedAtRef.current;
    try {
      if (isAnonymous) {
        // Grade in-browser from the publicSample answer key; stash the
        // attempt so email-gate can claim it on signup.
        const key = anonAnswersRef.current.get(current.id);
        if (!key) throw new Error("question not found");
        const correct = selected === key.correctLabel;
        const anonResult: SubmitResult = {
          correct,
          correctLabel: key.correctLabel,
          explanation: key.explanation,
        };
        setResult(anonResult);
        setScore((s) => ({
          correct: s.correct + (correct ? 1 : 0),
          total: s.total + 1,
        }));
        recordAnonAttempt({
          questionId: current.id,
          answer: selected,
          msSpent,
        });
        void observa.event({
          eventType: "practice.question_answered",
          payload: {
            sessionId: sessionIdRef.current,
            questionId: current.id,
            topicId: current.topicId,
            selected,
            correct,
            msSpent,
            anonymous: true,
          },
        });
        return anonResult;
      }

      const res = await trpc.questions.submit.mutate({
        questionId: current.id,
        answer: selected,
        msSpent,
        sessionId: sessionIdRef.current,
      });
      setResult(res);
      setScore((s) => ({
        correct: s.correct + (res.correct ? 1 : 0),
        total: s.total + 1,
      }));
      void observa.event({
        eventType: "practice.question_answered",
        payload: {
          sessionId: sessionIdRef.current,
          questionId: current.id,
          topicId: current.topicId,
          selected,
          correct: res.correct,
          msSpent,
        },
      });
      return res;
    } catch (err) {
      setError(err instanceof Error ? err.message : "submit failed");
      return null;
    } finally {
      setSubmitting(false);
    }
  }

  async function loadDiagnostic(): Promise<boolean> {
    // Re-check entitlement after the just-submitted attempts; if
    // EXHAUSTED, navigate to /diagnostic. Returns true when the caller
    // should skip its own navigation.
    //
    // Fail-CLOSED for FREE-tier batches: if this re-check itself fails
    // (network/transient) for a user who was on the free tier this batch,
    // route to /diagnostic anyway rather than falling through to the done
    // screen. After the last free answer the user is at the paywall
    // boundary, so a swallowed error must not dump them on a fabricated
    // "done" screen that skips the diagnostic/paywall. PAID users can't be
    // EXHAUSTED, so a transient failure for them just falls through to done.
    if (!vertical.verticalSlug) return false;
    const q: Record<string, string> = { vertical: vertical.verticalSlug };
    if (vertical.jurisdictionCode) {
      q.jurisdiction = vertical.jurisdictionCode;
    }
    const wasFreeTier = entitlement?.tier === "FREE";
    try {
      const ent = await trpc.entitlement.check.query({
        verticalSlug: vertical.verticalSlug,
        jurisdictionCode: vertical.jurisdictionCode ?? undefined,
      });
      if (ent.tier !== "EXHAUSTED") return false;
      router.replace({ pathname: "/diagnostic", params: q });
      return true;
    } catch {
      if (!wasFreeTier) return false;
      router.replace({ pathname: "/diagnostic", params: q });
      return true;
    }
  }

  function next(onAdvance: () => void): void {
    if (!questions) return;
    if (index + 1 >= questions.length) {
      void observa.event({
        eventType: "practice.session_completed",
        payload: {
          sessionId: sessionIdRef.current,
          totalAnswered: score.total,
          totalCorrect: score.correct,
          anonymous: isAnonymous,
        },
      });
      if (isAnonymous) {
        // Free sample done → the "save your progress" email gate, which
        // claims these attempts on signup. Forward the credential context
        // so email-gate keeps the vertical/jurisdiction.
        router.replace({
          pathname: "/email-gate",
          params: {
            vertical: vertical.verticalSlug,
            ...(vertical.jurisdictionCode
              ? { jurisdiction: vertical.jurisdictionCode }
              : {}),
          },
        });
        return;
      }
      void (async () => {
        const exhausted = await loadDiagnostic();
        if (exhausted) return;
        router.replace({
          pathname: "/practice/done",
          params: { sessionId: sessionIdRef.current },
        });
      })();
      return;
    }
    setIndex((i) => i + 1);
    setSelected(null);
    setResult(null);
    startedAtRef.current = Date.now();
    onAdvance();
  }

  return {
    questions,
    current,
    index,
    selected,
    setSelected,
    result,
    submitting,
    score,
    error,
    setError,
    entitlement,
    isAnonymous,
    sessionIdRef,
    submit,
    next,
  };
}
